import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { toast } from "react-toastify";
import { CreateMemberRequest } from "../features/members/contracts/CreateMemberRequest";
import { UpdateMemberRequest } from "../features/members/contracts/UpdateMemberRequest";
import {
  createMember,
  updateMember,
  fetchMemberById,
  fetchGroups,
  createGroup,
  addMemberToGroup,
  removeMemberFromGroup,
} from "../features/members/slice-extensions/members-slice-extension";
import {
  selectCurrentMember,
  selectGroups,
  selectMembersStatus,
} from "../features/members/members-slice";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { Group } from "../features/members/models/Group";
import { ROUTES } from "../routes";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import Tabs from "../components/Tabs";
import Input from "../components/input/Input";
import Textarea from "../components/textarea/Textarea";

const CreateEditMember: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const currentMember = useAppSelector(selectCurrentMember);
  const allGroups = useAppSelector(selectGroups);
  const membersStatus = useAppSelector(selectMembersStatus);

  const isEditMode = Boolean(id);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [groupSearchText, setGroupSearchText] = useState("");
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  // Errors
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string }>({});

  // Tabs
  const tabs = ["Member", "Group"];
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // Fetch groups on mount
  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  // Fetch member in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchMemberById(id));
    }
  }, [isEditMode, id, dispatch]);

  // Populate form when member loads in edit mode
  useEffect(() => {
    if (isEditMode && currentMember) {
      setFirstName(currentMember.firstName || "");
      setLastName(currentMember.lastName || "");
      setEmail(currentMember.email || "");
      setNotes(currentMember.notes || "");
      setSelectedGroups(currentMember.groups || []);
    }
  }, [isEditMode, currentMember]);

  // Handle tab selection
  const handleTabSelect = (index: number) => {
    if (index === 1) {
      navigate(ROUTES.CREATE_GROUP);
      return;
    }
    setSelectedTabIndex(index);
  };

  // Filtered groups for dropdown
  const filteredGroups = useMemo(() => {
    const selectedIds = new Set(selectedGroups.map((g) => g.id));
    return allGroups.filter((group) => {
      if (selectedIds.has(group.id)) return false;
      if (!groupSearchText.trim()) return true;
      return group.name.toLowerCase().includes(groupSearchText.toLowerCase());
    });
  }, [allGroups, selectedGroups, groupSearchText]);

  // Check if search text could create a new group (no exact name match exists)
  const canCreateGroup = useMemo(() => {
    const trimmed = groupSearchText.trim();
    if (!trimmed) return false;
    return !allGroups.some(
      (g) => g.name.toLowerCase() === trimmed.toLowerCase()
    );
  }, [groupSearchText, allGroups]);

  // Create a new group inline and add it to the selection
  const handleCreateAndSelectGroup = async () => {
    const name = groupSearchText.trim();
    if (!name || isCreatingGroup) return;
    setIsCreatingGroup(true);
    try {
      const result = await dispatch(
        createGroup({ name, description: null, tags: [] })
      );
      if (createGroup.fulfilled.match(result)) {
        const newGroup = result.payload;
        setSelectedGroups((prev) => [...prev, newGroup]);
        setGroupSearchText("");
        setShowGroupDropdown(false);
        toast.success(`Group "${name}" created!`);
        // Refresh groups list so it appears in future searches
        dispatch(fetchGroups());
      } else {
        toast.error("Failed to create group.");
      }
    } catch {
      toast.error("Failed to create group.");
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // Add group to selection
  const handleSelectGroup = (group: Group) => {
    setSelectedGroups((prev) => [...prev, group]);
    setGroupSearchText("");
    setShowGroupDropdown(false);
  };

  // Remove group from selection
  const handleRemoveGroup = (groupId: string) => {
    setSelectedGroups((prev) => prev.filter((g) => g.id !== groupId));
  };

  // Get group abbreviation (first 2 letters uppercase)
  const getGroupAbbreviation = (name: string): string => {
    return name.substring(0, 2).toUpperCase();
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { firstName?: string; lastName?: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = "First name is required.";
    }
    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (isEditMode && id && currentMember) {
        // Update member
        const request: UpdateMemberRequest = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          primaryLanguageCode: "en",
          timeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timeZoneUtcOffsetHours: new Date().getTimezoneOffset() / -60,
          email: email.trim() || null,
          currency: "USD",
          notes: notes.trim() || null,
        };

        const result = await dispatch(updateMember({ id, request }));

        if (updateMember.fulfilled.match(result)) {
          // Handle group changes
          const currentGroupIds = new Set(
            (currentMember.groups || []).map((g) => g.id)
          );
          const selectedGroupIds = new Set(selectedGroups.map((g) => g.id));

          // Add new groups
          const groupsToAdd = selectedGroups.filter(
            (g) => !currentGroupIds.has(g.id)
          );
          for (const group of groupsToAdd) {
            await dispatch(
              addMemberToGroup({ groupId: group.id, memberId: id })
            );
          }

          // Remove old groups
          const groupsToRemove = (currentMember.groups || []).filter(
            (g) => !selectedGroupIds.has(g.id)
          );
          for (const group of groupsToRemove) {
            await dispatch(
              removeMemberFromGroup({ groupId: group.id, memberId: id })
            );
          }

          toast.success("Member updated successfully!");
          navigate(ROUTES.MEMBER_DETAILS.replace(":id", id));
        } else {
          toast.error("Failed to update member. Please try again.");
        }
      } else {
        // Create member
        const request: CreateMemberRequest = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          primaryLanguageCode: "en",
          timeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timeZoneUtcOffsetHours: new Date().getTimezoneOffset() / -60,
          email: email.trim() || null,
          currency: "USD",
          notes: notes.trim() || null,
          tags: [],
        };

        const result = await dispatch(createMember(request));

        if (createMember.fulfilled.match(result)) {
          const newMember = result.payload;

          // Add groups to the newly created member
          for (const group of selectedGroups) {
            await dispatch(
              addMemberToGroup({ groupId: group.id, memberId: newMember.id })
            );
          }

          toast.success("Member created successfully!");
          navigate(ROUTES.MEMBER_DETAILS.replace(":id", newMember.id));
        } else {
          toast.error("Failed to create member. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error saving member:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && id) {
      navigate(ROUTES.MEMBER_DETAILS.replace(":id", id));
    } else {
      navigate(ROUTES.MEMBERS);
    }
  };

  const isFetching = membersStatus === LoadingStatus.Loading && isEditMode;

  return (
    <section>
      <div className="flex h-full gap-4">
        {/* Left Side */}
        <LeftMenu />
        {/* Right Side */}
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[694px]">
            {/* Breadcrumbs */}
            <Breadcrumbs />

            {/* Content */}
            <div className="space-y-4 py-6 lg:space-y-6">
              <h1 className="text-grey-10 dark:text-white">
                {isEditMode ? "Edit Member" : "Create Member"}
              </h1>

              {/* Tabs */}
              <Tabs
                tabs={tabs}
                selectedIndex={selectedTabIndex}
                onSelect={handleTabSelect}
              />

              {/* Loading state */}
              {isFetching && (
                <div
                  className="flex items-center gap-2 text-grey-30 dark:text-grey-70"
                  role="status"
                  aria-live="polite"
                >
                  <div
                    className="animate-spin rounded-full h-5 w-5 border-2 border-primary-40 border-t-transparent"
                    aria-hidden="true"
                  ></div>
                  <span>Loading member...</span>
                </div>
              )}

              {/* Form */}
              {(!isFetching || !isEditMode) && (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6 lg:space-y-4"
                >
                  {/* Member Info Section */}
                  <div className="space-y-6 lg:space-y-5">
                    <h3 className="text-grey-10 dark:text-grey-90 font-bold">
                      Member Info
                    </h3>

                    <Input
                      label="First Name"
                      type="text"
                      name="first_name"
                      id="first_name"
                      placeholder="Enter first name here..."
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (errors.firstName) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.firstName;
                            return next;
                          });
                        }
                      }}
                      error={errors.firstName}
                      disabled={isSubmitting}
                    />

                    <Input
                      label="Last Name"
                      type="text"
                      name="last_name"
                      id="last_name"
                      placeholder="Enter last name here..."
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (errors.lastName) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.lastName;
                            return next;
                          });
                        }
                      }}
                      error={errors.lastName}
                      disabled={isSubmitting}
                    />

                    <Input
                      label="Email"
                      type="email"
                      name="email"
                      id="email"
                      placeholder="Enter email here..."
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Divider */}
                  <hr className="border-grey-70 dark:border-primary-20" />

                  {/* Group Enrollment Section */}
                  <div className="space-y-4">
                    <h3 className="text-grey-10 dark:text-grey-90 font-bold">
                      Group Enrollment ({selectedGroups.length} Groups)
                    </h3>
                    <p className="text-grey-30 dark:text-grey-70 text-sm">
                      Add the member to one or more groups.
                    </p>

                    {/* Group Search */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-grey-30 dark:text-white mb-1">
                        Add Groups (optional):
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          className="input"
                          placeholder="Search groups..."
                          value={groupSearchText}
                          onChange={(e) => {
                            setGroupSearchText(e.target.value);
                            setShowGroupDropdown(true);
                          }}
                          onFocus={() => setShowGroupDropdown(true)}
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Group Dropdown */}
                      {showGroupDropdown && (filteredGroups.length > 0 || canCreateGroup) && (
                        <div className="absolute z-10 w-full mt-1 max-h-48 overflow-y-auto rounded-lg border border-grey-70 dark:border-primary-20 bg-white dark:bg-primary-20 shadow-lg">
                          {filteredGroups.map((group) => (
                            <button
                              key={group.id}
                              type="button"
                              className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-grey-10 dark:text-grey-90 transition-colors"
                              onClick={() => handleSelectGroup(group)}
                            >
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-80 dark:bg-primary-30 text-primary-20 dark:text-primary-90 text-xs font-semibold shrink-0 leading-none">
                                {getGroupAbbreviation(group.name)}
                              </span>
                              <span className="text-sm leading-none">{group.name}</span>
                            </button>
                          ))}
                          {canCreateGroup && (
                            <button
                              type="button"
                              className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-primary-40 dark:text-primary-90 transition-colors border-t border-grey-70 dark:border-primary-30"
                              onClick={handleCreateAndSelectGroup}
                              disabled={isCreatingGroup}
                            >
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-90 dark:bg-primary-30 text-primary-40 dark:text-primary-90 shrink-0">
                                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path className="fill-current" d="M12 6C12.5523 6 13 6.44772 13 7V11H17C17.5523 11 18 11.4477 18 12C18 12.5523 17.5523 13 17 13H13V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V13H7C6.44772 13 6 12.5523 6 12C6 11.4477 6.44772 11 7 11H11V7C11 6.44772 11.4477 6 12 6Z" />
                                </svg>
                              </span>
                              <span className="text-sm leading-none">
                                {isCreatingGroup ? "Creating..." : `Create "${groupSearchText.trim()}"`}
                              </span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Selected Groups */}
                    {selectedGroups.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedGroups.map((group) => (
                          <div
                            key={group.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-90 dark:bg-primary-20 text-primary-30 dark:text-primary-90 text-sm font-medium"
                          >
                            <span>{group.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveGroup(group.id)}
                              className="ml-1 text-primary-40 dark:text-primary-80 hover:text-primary-20 dark:hover:text-white"
                              disabled={isSubmitting}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Show All Groups link */}
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.GROUPS)}
                      className="text-primary-40 dark:text-primary-90 text-sm hover:underline"
                    >
                      Show All Groups &gt;
                    </button>
                  </div>

                  {/* Divider */}
                  <hr className="border-grey-70 dark:border-primary-20" />

                  {/* Member Notes Section */}
                  <div className="space-y-6 lg:space-y-5">
                    <h3 className="text-grey-10 dark:text-grey-90 font-bold">
                      Member Notes
                    </h3>

                    <Textarea
                      label="Notes (optional)"
                      name="member_notes"
                      id="member_notes"
                      placeholder="Add some member details here..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-start gap-3">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? isEditMode
                          ? "Saving..."
                          : "Creating..."
                        : isEditMode
                        ? "Save Changes"
                        : "Create Member"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreateEditMember;
